import PropTypes from 'prop-types';
import React from 'react';
import md5 from 'md5';
import { bindActionCreators } from 'redux';

import Jaws from 'oms-jaws';
import { BASE_URL } from 'constants/Jaws';

import * as ComponentActions from 'actions/component';

const { object, string } = PropTypes;

/**
 * The base class of a jaws.js component. Extend this
 * class to enable your component to stream data.
 *
 * You also need to connect to redux, do this by using
 * the `jawsConnect` utility method.
 *
 * If you need to both subcribe to serverside jaws and
 * something else in redux (like user data or
 * watchlists), then you need to  manually connect your
 * component to redux. Do this by following the example
 * of jawsConnect, and then add whatever else you need
 * inside the component to the return object of
 * `mapStateToProps`.
 *
 * You may force HTTP connection by simply passing the
 * forceHTTPConnection parameter to the JawsComponent
 * constructor. Do this by including the following in
 * your component:
 *
 * ```javascript
 * constructor(props, context, reactUpdateQueue) {
 *   const forceHTTPConnection = true;
 *   super(props, context, reactUpdateQueue, forceHTTPConnection);
 * }
 * ```
 */
export default class JawsComponent extends React.Component {
  subscriptionId = null;
  queue = {};

  constructor(props, context, reactUpdateQueue, forceHTTPConnection = false) {
    super(props, context);

    this.forceHTTPConnection = forceHTTPConnection;
    this.jaws = forceHTTPConnection
      ? new Jaws(props.jaws?.url || BASE_URL, false)
      : props.jaws;

    this.componentId = props.componentId;

    const { dispatch } = this.props;
    const actions = bindActionCreators(ComponentActions, dispatch);
    this.actions = actions;

    this.handleNewEvent = this.handleNewEvent.bind(this);
    this.handleUpdateEvent = this.handleUpdateEvent.bind(this);
    this.handleDeleteEvent = this.handleDeleteEvent.bind(this);
    this.handleChannelToken = this.handleChannelToken.bind(this);
    this.handleMetaEvent = this.handleMetaEvent.bind(this);
    this.subscribe = this.subscribe.bind(this);
  }

  static propTypes = {
    jaws: object.isRequired,
    items: object.isRequired,
    componentId: string.isRequired,
  };

  /** When the component mounts, create a subscription and bind callbacks
   */
  componentDidMount() {
    this.subscribe();
  }

  /** Subscribe
   */
  subscribe() {
    this.resetQueue();
    let spec = this.spec || this.props.spec;
    const subId = (this.subscriptionId = md5(JSON.stringify(spec)));

    const overrides = {
      channel: subId,
    };

    spec = Object.assign({}, spec, overrides);
    const sub = this.jaws.subscribe(subId, spec);

    sub.bind('new', this.handleNewEvent);
    sub.bind('update', this.handleUpdateEvent);
    sub.bind('delete', this.handleDeleteEvent);
    sub.bind('channel_token_received', this.handleChannelToken);
    sub.bind('meta', this.handleMetaEvent);
  }

  /** Unsubscribe from updates
   */
  unsubscribe() {
    this.resetQueue();
    this.actions.deleteAllItems(this.componentId);
    this.jaws.unsubscribe(this.subscriptionId);
    this.subscriptionId = null;
  }

  /** Convenience method for resubscribing.
   */
  resubscribe() {
    if (this.subscriptionId) {
      this.unsubscribe();
    }
    this.subscribe();
  }

  componentWillUnmount() {
    this.unsubscribe();
  }

  /** Create an item in state
   */
  handleNewEvent(data) {
    if (!this.props.initialized) {
      return this.addToQueue(data);
    }

    return this.actions.addItem(this.componentId, data);
  }

  addToQueue(data) {
    this.queue[data.key] = data;
  }

  flushQueue() {
    this.actions.addItems(this.componentId, this.queue);
    this.resetQueue();
  }

  resetQueue() {
    this.queue = {};
  }

  /** Update an item in state
   */
  handleUpdateEvent(data) {
    this.actions.updateItem(this.componentId, data);
  }

  /** Delete an item from state
   */
  handleDeleteEvent(data) {
    this.actions.deleteItem(this.componentId, data);
  }

  /** Reset state
   */
  handleChannelToken() {
    this.actions.addComponent(this.componentId);
    this.actions.deleteAllItems(this.componentId);
  }

  handleMetaEvent(data) {
    if (data.values.initial_data_sent) {
      this.flushQueue();
      this.actions.componentInitialized(this.componentId);
    }
    if (data.values.size) {
      clearTimeout(this.totalSizeTimeout);
      this.totalSizeTimeout = setTimeout(() => {
        this.actions.setTotalSize(this.componentId, data.values.size);
      }, 10);
    }
  }
}
